/**
 * ESCHER FALLS — StoreKit の実装。`iap.ts` の差し口に差し込む中身。
 *
 * ネイティブ側は `ios/App/App/StoreKitPlugin.swift`（StoreKit 2）。
 * ここはその薄い包みで、**判断は一切しない**——何を売るか、買えたら何を渡すかは
 * `iap.ts` と `main.ts` が決める。
 *
 * ## 差せないときは差さない
 * ブラウザやプラグインの無い環境では `register()` が何もしないで返る。
 * 差さなければ `isStoreAvailable()` が false のままなので、
 * 売り場は節ごと現れない（iap.ts 規則1）。**押せない買い物ボタンを出さない。**
 */

import { Capacitor, registerPlugin } from '@capacitor/core';
import {
  PRODUCTS,
  setPurchaseProvider,
  type PurchaseProvider,
} from './iap';

interface StoreKitNative {
  getProducts(options: { ids: string[] }): Promise<{
    products: Array<{ id: string; price: string; title: string }>;
  }>;
  purchase(options: { id: string }): Promise<{ purchased: boolean; reason?: string }>;
  restore(): Promise<{ owned: string[] }>;
}

const StoreKit = registerPlugin<StoreKitNative>('StoreKit');

/** ストアが返した表示価格。地域ごとに違うので**必ずこちらを表示する。** */
const prices = new Map<string, string>();
let ready = false;

/**
 * StoreKit を差し込む。**起動時に一度だけ呼ぶ。**
 *
 * 商品情報が引けるまで `isReady()` は false のままにしてある。
 * 価格が分からないうちに売り場を出すと、値段の欄が空のまま並ぶことになる。
 */
export async function registerStoreKit(): Promise<void> {
  // ネイティブが居ない環境（ブラウザ）では差さない。
  if (Capacitor.getPlatform() !== 'ios') return;

  const provider: PurchaseProvider = {
    isReady: () => ready,
    priceOf: (id) => prices.get(id) ?? null,
    purchase: async (id) => {
      const res = await StoreKit.purchase({ id });
      return res.purchased;
    },
    restore: async () => {
      const res = await StoreKit.restore();
      return res.owned;
    },
  };
  setPurchaseProvider(provider);

  try {
    const res = await StoreKit.getProducts({ ids: PRODUCTS.map((p) => p.id) });
    for (const p of res.products) prices.set(p.id, p.price);
    // **1つも引けなかったら売り場を出さない。** 商品が App Store Connect に
    // 未登録か審査中で、押しても必ず失敗する状態だから。
    ready = res.products.length > 0;
    console.info(`[storekit] 商品 ${res.products.length} 件`);
  } catch (e) {
    // 引けなくても遊びは続く。売り場が出ないだけ。
    ready = false;
    // **黙って握り潰さない。** 以前ここが無言だったせいで、
    // ネイティブ側のプラグインが未登録で**課金が一度も動いていなかった**ことに
    // 長く気づかなかった。売り場が出ないのと、繋がっていないのは別の事故で、
    // 外から見分けがつかない。実機のコンソールに理由を残す。
    console.error('[storekit] getProducts に失敗', String(e));
  }
}

/**
 * 非消耗品の持ち主かどうかを取り直す。**起動のたびに呼ぶ。**
 *
 * 端末を変えた人・アプリを入れ直した人は、買ったものが手元の記録に無い。
 * `Transaction.currentEntitlements` が唯一の正なので、そこから戻す。
 */
export async function restoreOwned(): Promise<readonly string[]> {
  if (Capacitor.getPlatform() !== 'ios') return [];
  try {
    const res = await StoreKit.restore();
    return res.owned;
  } catch {
    return [];
  }
}
